import { Fragment, useMemo } from 'react'
import { Listbox, Transition } from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/solid'
import { cn } from 'cn'
import format from 'date-fns/format'
import { useCartUpdatePickupTimeMutation } from 'components/pages/CartPage/TimePicker/types/cartUpdatePickupTime'
import { useCartRemovePickupTimeMutation } from 'components/pages/CartPage/TimePicker/types/cartRemovePickupTime'
import Notification from 'components/Notification'

type TimeOption = {
  label: string
  date: number | null
}

type Props = {
  cartRefetch: () => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  locationAvailableOrderTimes: any[]
  uuid: string
  delayedPickupEnabled: boolean
  asapOrdersEnabled: boolean
  selectedValue: string | null
  pickupTimeSelected: boolean
  error?: string | null
  resetPickupTimeError: () => void
}

const TimePicker = ({
  cartRefetch,
  locationAvailableOrderTimes,

  uuid,
  delayedPickupEnabled,
  asapOrdersEnabled,
  selectedValue,
  pickupTimeSelected,
  error,
  resetPickupTimeError
}: Props) => {
  const availableTimeOptions: TimeOption[] = useMemo(() => {
    const result: TimeOption[] = delayedPickupEnabled
      ? locationAvailableOrderTimes.map((time) => ({
          label: format(new Date(time), 'E L/d, h:mm aaa '),
          date: new Date(time).getTime()
        }))
      : []
    if (asapOrdersEnabled) {
      result?.unshift({
        label: 'As soon as ready',
        date: null
      })
    }
    return result
  }, [locationAvailableOrderTimes, asapOrdersEnabled, delayedPickupEnabled])

  const [cartUpdatePickupTimeMutation] = useCartUpdatePickupTimeMutation({})

  const [cartRemovePickupTimeMutation] = useCartRemovePickupTimeMutation({})

  const updatePickupTime = async ({ time }: { time: number }) => {
    const response = await cartUpdatePickupTimeMutation({
      notifications: {
        onError: true
      },
      blockUi: true,
      variables: {
        cartUuid: uuid,
        pickupTime: new Date(time).toISOString()
      }
    })

    if (!response) return

    await cartRefetch()
  }

  const removePickupTime = async () => {
    const response = await cartRemovePickupTimeMutation({
      notifications: {
        onError: true
      },
      blockUi: true,
      variables: {
        cartUuid: uuid
      }
    })
    if (!response) return
    await cartRefetch()
  }

  const onChangePickupTime = async (time: number | null) => {
    resetPickupTimeError()
    if (time) {
      await updatePickupTime({ time })
    } else {
      await removePickupTime()
    }
  }

  const formattedValue = selectedValue
    ? new Date(selectedValue).getTime()
    : null

  if (availableTimeOptions.length === 0) {
    return (
      <div id="pickupTimeSelector">
        <div className="block text-sm font-medium text-gray-800">
          Pickup Time:
        </div>
        <div className="mt-1" role="status">
          <Notification variant="warning">
            This food truck appears to be busy and has no available pickup time
            slots, so you cannot place an order online right now. You can try
            your luck ordering in person at the truck.
          </Notification>
        </div>
      </div>
    )
  }

  return (
    <div id="pickupTimeSelector">
      <Listbox onChange={onChangePickupTime} value={formattedValue}>
        {({ open }) => (
          <>
            <Listbox.Label className="block text-sm font-medium text-gray-800">
              Pickup Time:
            </Listbox.Label>
            <div className="mt-1 relative">
              <Listbox.Button
                className={cn(
                  'h-12 relative w-full bg-white border  rounded-md shadow-xs pl-3 pr-10 py-3 text-left cursor-default focus:outline-hidden focus:ring-1 sm:text-sm',
                  {
                    'border-gray-300 focus:ring-orange-500 focus:border-orange-700':
                      !error,
                    'border-red-300': error
                  }
                )}
              >
                {pickupTimeSelected ? (
                  <span className="block truncate">
                    {
                      availableTimeOptions.find(
                        (o) => o.date === formattedValue
                      )?.label
                    }
                  </span>
                ) : (
                  <span className="text-gray-400 font-light">
                    Select a time...
                  </span>
                )}

                <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <ChevronUpDownIcon
                    aria-hidden="true"
                    className="h-5 w-5 text-gray-400"
                  />
                </span>
              </Listbox.Button>

              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                show={open}
              >
                <Listbox.Options className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black/5 overflow-auto focus:outline-hidden sm:text-sm">
                  {availableTimeOptions.map((timeOption) => (
                    <Listbox.Option
                      key={timeOption.label}
                      className={({ active }) =>
                        cn(
                          active ? 'text-white bg-orange-400' : 'text-gray-900',
                          'cursor-default select-none relative py-2 pl-8 pr-4'
                        )
                      }
                      value={timeOption.date}
                    >
                      {({ selected, active }) => (
                        <>
                          <span
                            className={cn(
                              selected ? 'font-semibold' : 'font-normal',
                              'block truncate'
                            )}
                          >
                            {timeOption.label}
                          </span>

                          {selected ? (
                            <span
                              className={cn(
                                active ? 'text-white' : 'text-orange-600',
                                'absolute inset-y-0 left-0 flex items-center pl-1.5'
                              )}
                            >
                              <CheckIcon
                                aria-hidden="true"
                                className="h-5 w-5"
                              />
                            </span>
                          ) : null}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
            {error && (
              <div className="mt-2">
                <Notification variant="error">{error}</Notification>
              </div>
            )}
          </>
        )}
      </Listbox>

      <div className="text-xs font-light mt-2 text-gray-800 text-justify">
        The pickup time selected cannot be changed once the order is placed. If
        you choose &quot;As soon as ready&quot;, the order will be ready as soon
        as the truck can make it.
      </div>
    </div>
  )
}

export default TimePicker
